import { isEqual } from 'lodash';

import { Control } from 'dashboard/components/FormControlPilledInput/types';
import { NodeId } from 'dashboard/models/graph';
import { ServerQueryCursor } from 'dashboard/models/graph-backend';
import {
  ByClusterCreatedAtConditional,
  ClustersQuery,
  Conditional,
  DateConditional,
  GraphQueryBody,
  hasEmptyVariable,
  isConditionalValid,
  LeftConstants,
  NHopsFromAccountQuery,
  Operator,
  QueryConditionalConfig,
  QueryConfig,
  QueryType,
  TextValue,
  ValueType,
  VariableValue,
} from 'dashboard/models/graph-query';
import { assertNever } from 'lib/utils/types';

export interface ClusterQueryData {
  queryType: QueryType.ByCluster;
  createdAtConditional: ByClusterCreatedAtConditional;
  sharedPropertiesConditional: Conditional;
  variables: VariableValue[];
  minimumClusterSize: number;
  numberOfHops: number;
}

export interface NHopsQueryData {
  queryType: QueryType.NHopsFromAccount;
  startingSetConditionals: Conditional[];
  sharedPropertiesConditional: Conditional;
  traversalConditionals: Conditional[];
  variables: VariableValue[];
  numberOfHops: number;
}

export interface FormState {
  queryData: ClusterQueryData | NHopsQueryData;
  nodeLimit: number;
}

export const defaultFormState = (queryConfig: QueryConfig): FormState => {
  return {
    queryData: {
      queryType: QueryType.NHopsFromAccount,
      startingSetConditionals: [
        {
          left: queryConfig[0].left.key,
          // use the first operator as defined by Object.keys(...), with undefined value
          operator: Object.keys(queryConfig[0].operatorToRight)[0] as Operator,
          right: [],
        } as Conditional,
      ],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sharedPropertiesConditional: null,
      traversalConditionals: [],
      variables: [],
      numberOfHops: 2,
    },
    nodeLimit: 100,
  };
};

export const defaultSharedPropertiesConditional = (
  sharedPropertiesConfig: QueryConditionalConfig,
): Conditional => {
  if (sharedPropertiesConfig.operatorToRight[Operator.IncludesAnyOf]) {
    // default to the "any property" selection
    return {
      left: sharedPropertiesConfig.left.key,
      operator: Operator.IncludesAnyOf,
      right: [
        {
          'raw-value': '*',
          value: '*',
          type: ValueType.Text,
        },
      ],
    } as Conditional;
  } else {
    return {
      left: sharedPropertiesConfig.left.key,
      operator: Object.keys(sharedPropertiesConfig.operatorToRight)[0] as Operator,
      right: [],
    } as Conditional;
  }
};

export const formStateToQuery = (
  formState: FormState,
  knownNodeIds: NodeId[],
  expandabilityLimit: number,
  startCursor: ServerQueryCursor,
  sharedPropertiesConfig: QueryConditionalConfig,
): GraphQueryBody => {
  let sharedPropertiesConditional: Conditional = formState.queryData.sharedPropertiesConditional;

  // Do the wildcard substitution, if relevant.
  if (
    isEqual(sharedPropertiesConditional.right[0], {
      'raw-value': '*',
      value: '*',
      type: 'text',
    })
  ) {
    const anyOfOptions =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sharedPropertiesConfig.operatorToRight[Operator.IncludesAnyOf].type === Control.Select
        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          sharedPropertiesConfig.operatorToRight[Operator.IncludesAnyOf].options
        : [];

    const anyOfConditionals: TextValue[] = anyOfOptions.map(
      (option: { value: string; label: string }) => ({
        'raw-value': option.value,
        value: option.value,
        type: ValueType.Text,
      }),
    );

    sharedPropertiesConditional = {
      left: 'shared_properties',
      operator: Operator.IncludesAnyOf,
      right: anyOfConditionals,
    };
  }

  switch (formState.queryData.queryType) {
    case QueryType.ByCluster: {
      const clustersQuery: ClustersQuery = {
        type: QueryType.ByCluster,
        conditionals: [
          // Convert the magic by-clusters left to the actual 'created_at' value.
          {
            ...formState.queryData.createdAtConditional,
            left: 'created_at',
          },
          sharedPropertiesConditional,
        ],
        variables: formState.queryData.variables,
        'known-node-ids': knownNodeIds,
        'node-limit': formState.nodeLimit,
        'expandability-limit': expandabilityLimit,
        'min-cluster-size': formState.queryData.minimumClusterSize,
        'num-hops': formState.queryData.numberOfHops,
        'start-cursor': startCursor,
      };
      return clustersQuery;
    }
    case QueryType.NHopsFromAccount: {
      const nHopsQuery: NHopsFromAccountQuery = {
        type: QueryType.NHopsFromAccount,
        conditionals: [...formState.queryData.startingSetConditionals, sharedPropertiesConditional],
        'traversal-conditionals': formState.queryData.traversalConditionals,
        variables: formState.queryData.variables,
        'known-node-ids': knownNodeIds,
        'node-limit': formState.nodeLimit,
        'num-hops': formState.queryData.numberOfHops,
        'expandability-limit': expandabilityLimit,
        'start-cursor': startCursor,
      };
      return nHopsQuery;
    }
    default: {
      assertNever(formState.queryData);
    }
  }
};

export const queryToFormState = (queryBody: GraphQueryBody): FormState => {
  switch (queryBody.type) {
    case QueryType.ByCluster: {
      if (queryBody.conditionals.length !== 2) {
        throw new Error(`Expected 2 conditionals, got ${queryBody.conditionals.length}`);
      }
      const sharedPropertiesConditional = queryBody.conditionals.find(
        (c) => c.left === 'shared_properties',
      );
      const createdAtConditional = queryBody.conditionals.find(
        (c) => c.left !== 'shared_properties',
      );
      return {
        queryData: {
          queryType: QueryType.ByCluster,
          createdAtConditional: {
            ...(createdAtConditional as DateConditional),
            left: LeftConstants.ByClusterCreatedAt,
          },
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          sharedPropertiesConditional,
          variables: queryBody.variables,
          minimumClusterSize: queryBody['min-cluster-size'],
          numberOfHops: queryBody['num-hops'],
        },
        nodeLimit: queryBody['node-limit'],
      };
    }
    case QueryType.NHopsFromAccount: {
      const sharedPropertiesConditional = queryBody.conditionals.find(
        (c) => c.left === 'shared_properties',
      );
      const startingSetConditionals = queryBody.conditionals.filter(
        (c) => c.left !== 'shared_properties',
      );
      return {
        queryData: {
          queryType: QueryType.NHopsFromAccount,
          startingSetConditionals,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          sharedPropertiesConditional,
          traversalConditionals: queryBody['traversal-conditionals'] || [],
          variables: queryBody.variables,
          numberOfHops: queryBody['num-hops'],
        },
        nodeLimit: queryBody['node-limit'],
      };
    }
    default: {
      throw new Error(`Invalid query type: ${queryBody.type}`);
    }
  }
};

// if any right value is missing or errored, isValid will be false
export const isFormStateValid = (formState: FormState): boolean => {
  let allConditionals: Conditional[];
  switch (formState.queryData.queryType) {
    case QueryType.ByCluster: {
      allConditionals = [
        formState.queryData.createdAtConditional,
        formState.queryData.sharedPropertiesConditional,
      ];
      break;
    }
    case QueryType.NHopsFromAccount: {
      allConditionals = [
        ...formState.queryData.startingSetConditionals,
        formState.queryData.sharedPropertiesConditional,
      ];
      break;
    }
    default: {
      assertNever(formState.queryData);
    }
  }

  return (
    allConditionals.every((c) => c.right.length > 0) && allConditionals.every(isConditionalValid)
  );
};

// Used for disabling the run button if/when there's a variable in the query.
export const isNotRunnable = (formState: FormState): boolean => {
  let conditionals: Conditional[];
  switch (formState.queryData.queryType) {
    case QueryType.ByCluster: {
      conditionals = [formState.queryData.createdAtConditional];

      // TODO: Generalize to formErrors hash tracking (keyed by input label/key)
      if (formState.queryData.minimumClusterSize < 1) {
        return true;
      }
      break;
    }
    case QueryType.NHopsFromAccount: {
      conditionals = [
        ...formState.queryData.startingSetConditionals,
        ...formState.queryData.traversalConditionals,
      ];

      break;
    }
    default: {
      assertNever(formState.queryData);
    }
  }

  return conditionals.some(
    (conditional) => hasEmptyVariable(conditional) || !isConditionalValid(conditional),
  );
};
